import { FC, memo, useState, useEffect } from "react";
import { useAppSelector } from "app/hooks";
import "dayjs/locale/es";
import dayjs, { Dayjs } from "dayjs";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useField, Field } from "formik";

type Props = {
  name: string;
  label: string;
  subSectionId: number | string;
};

const FieldDatePicker: FC<Props> = memo(({ name, label, subSectionId }) => {
  const [field, meta, helpers] = useField(name);
  const error = !!meta.error && meta.touched;
  const [fieldValue, setFieldValue] = useState<Dayjs | any>(dayjs(new Date()));

  const { answers } = useAppSelector((state) => state.survey) ?? [];
  const id = name.split(".")[2];
  const answer =
    answers.find((answer) => answer.questionID === id && answer.subSectionId === subSectionId)?.value ?? "";

  useEffect(() => {
    if (fieldValue) {
      field.onChange({
        target: {
          name: name,
          value: fieldValue,
        },
      });
    }
  }, [fieldValue]);

  useEffect(() => {
    if (answer) {
      helpers.setValue(answer);
      setFieldValue(answer);
    }
  }, [answer]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} locale='es'>
      <Stack spacing={2}>
        <Field
          as={DatePicker}
          label={label}
          minDate={dayjs("1950-01-01")}
          value={fieldValue}
          onChange={(newValue: any) => {
            const dateFormatted = dayjs(newValue).format("YYYY-MM-DD");
            if (dateFormatted === "Invalid Date") {
              setFieldValue("Invalid Date");
              return;
            }
            setFieldValue(dateFormatted);
          }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              name={name}
              margin='normal'
              error={error}
              value={fieldValue}
              helperText={meta.touched && meta.error}
            />
          )}
        />
      </Stack>
    </LocalizationProvider>
  );
});

export default FieldDatePicker;
